import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import arEG from 'antd/locale/ar_EG';
import { getInitialUiLanguage, LANGUAGE_STORAGE_KEY } from './i18n';

// 路由配置
import { adminRouteConfig } from './appRoutes';
import { getLoginPath, getStoredUser, isAuthenticated } from './utils/auth';

// 组件

// 所有页面直接导入（合并为一个文件）
import Login from './pages/Login';
import CsDesk from './pages/CsDesk';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AccountManagement from './pages/AccountManagement';
import MailManagement from './pages/MailManagement';
import UserManagement from './pages/UserManagement';
import MallManagement from './pages/MallManagement';
import PlatformCoinGrantManagement from './pages/PlatformCoinGrantManagement';
import BalanceManagement from './pages/BalanceManagement';
import ActivityManagement from './pages/ActivityManagement';
import AgentManagement from './pages/AgentManagement';
import Mall from './pages/Mall';
import ConfigManagement from './pages/ConfigManagement';
import ClientUpdateManagement from './pages/ClientUpdateManagement';
import Statistics from './pages/Statistics';
import ClientLogin from './pages/ClientLogin';
import AgentDashboard from './pages/AgentDashboard';
import MainLayout from './components/MainLayout';
import GiftManagement from './pages/GiftManagement';
import CdkManagement from './pages/CdkManagement';
import ItemManagement from './pages/ItemManagement';
import BubbleRewardManagement from './pages/BubbleRewardManagement';
import AvatarReviewManagement from './pages/AvatarReviewManagement';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentProviderManagement from './pages/PaymentProviderManagement';
import LotteryManagement from './pages/LotteryManagement';
import ServerMonitor from './pages/ServerMonitor';
import PromotionCenter from './pages/PromotionCenter';
import CheatAccountScan from './pages/CheatAccountScan';
import ItemClearManagement from './pages/ItemClearManagement';
import ManualRechargeManagement from './pages/ManualRechargeManagement';

const THEME_STORAGE_KEY = 'doomsday_admin_theme_mode';

const getInitialThemeMode = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
};

const ProtectedRoute = ({ children, requiredRole }) => {
  if (!isAuthenticated(requiredRole)) {
    return <Navigate to={getLoginPath(requiredRole)} replace />;
  }

  if (requiredRole) {
    const role = getStoredUser(requiredRole)?.role || null;
    if (role !== requiredRole) {
      return <Navigate to={getLoginPath(requiredRole)} replace />;
    }
  }

  return children;
};

// 组件映射
const ALL_COMPONENTS = {
  Login,
  Register,
  Dashboard,
  AccountManagement,
  MailManagement,
  UserManagement,
  MallManagement,
  PlatformCoinGrantManagement,
  BalanceManagement,
  ActivityManagement,
  AgentManagement,
  Mall,
  ConfigManagement,
  ClientUpdateManagement,
  Statistics,
  ClientLogin,
  AgentDashboard,
  MainLayout,
  GiftManagement,
  CdkManagement,
  ItemManagement,
  BubbleRewardManagement,
  AvatarReviewManagement,
  PaymentSuccess,
  PaymentProviderManagement,
  LotteryManagement,
  ServerMonitor,
  PromotionCenter,
  CheatAccountScan,
  ItemClearManagement,
  ManualRechargeManagement
};

function App() {
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [uiLanguage, setUiLanguage] = useState(getInitialUiLanguage);
  const isDarkMode = themeMode === 'dark';

  useEffect(() => {
    const rootNode = document.documentElement;
    rootNode.setAttribute('data-theme', themeMode);
    document.body.setAttribute('data-theme', themeMode);
    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  useEffect(() => {
    const isArabic = uiLanguage === 'ar-SA';
    document.documentElement.lang = isArabic ? 'ar-SA' : uiLanguage;
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
    document.body.dir = isArabic ? 'rtl' : 'ltr';
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, uiLanguage);
    } catch (error) {
      // Embedded browsers may disable persistent storage.
    }
  }, [uiLanguage]);

  const toggleThemeMode = useCallback(() => {
    setThemeMode((currentMode) => (currentMode === 'dark' ? 'light' : 'dark'));
  }, []);

  const appTheme = useMemo(() => ({
    algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: '#2563eb',
      borderRadius: 8,
      colorBgLayout: isDarkMode ? '#0b1120' : '#f1f5f9',
      colorBgContainer: isDarkMode ? '#111827' : '#ffffff',
      colorBorder: isDarkMode ? '#334155' : '#d9dfe8',
      colorText: isDarkMode ? '#e5e7eb' : '#0f172a',
      colorTextSecondary: isDarkMode ? '#cbd5e1' : '#475569'
    },
    components: {
      Layout: {
        headerBg: 'transparent',
        siderBg: isDarkMode ? '#111827' : '#ffffff'
      },
      Menu: {
        itemBorderRadius: 12
      }
    }
  }), [isDarkMode]);

  const antdLocale = uiLanguage === 'ar-SA' ? arEG : (uiLanguage === 'en-US' ? enUS : zhCN);
  const direction = uiLanguage === 'ar-SA' ? 'rtl' : 'ltr';

  return (
    <ConfigProvider locale={antdLocale} direction={direction} theme={appTheme}>
      <Router>
        <Routes>
          {/* 公开路由 */}
          <Route path="/login" element={<Login themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route path="/agent/login" element={<Login themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route path="/cs/login" element={<Login themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route
            path="/cs/desk"
            element={
              <ProtectedRoute requiredRole="agent">
                <CsDesk />
              </ProtectedRoute>
            }
          />
          <Route path="/agent/master-login" element={<Login themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route path="/agent/promoter-login" element={<Login themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route path="/agent.html" element={<Login themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route path="/master-agent.html" element={<Login themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route path="/register" element={<Register themeMode={themeMode} onToggleThemeMode={toggleThemeMode} language={uiLanguage} onLanguageChange={setUiLanguage} />} />
          <Route path="/promotion-center" element={<PromotionCenter />} />

          {/* 管理员路由 */}
          <Route
            path="/"
            element={
              <ProtectedRoute requiredRole="admin">
                <MainLayout themeMode={themeMode} onToggleThemeMode={toggleThemeMode} />
              </ProtectedRoute>
            }
          >
            {adminRouteConfig.map((route) => {
              const RouteComponent = ALL_COMPONENTS[route.componentKey];
              if (!RouteComponent) {
                return null;
              }

              if (!route.routePath) {
                return <Route key={route.key} index element={<RouteComponent />} />;
              }

              return (
                <Route
                  key={route.key}
                  path={route.routePath}
                  element={<RouteComponent />}
                />
              );
            })}
            <Route path="lottery" element={<Navigate to="/lottery-management" replace />} />
          </Route>

          {/* 代理路由 */}
          <Route
            path="/agent/promoter-dashboard"
            element={
              <ProtectedRoute requiredRole="agent">
                <AgentDashboard expectedAgentLevel={1} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/master-dashboard"
            element={
              <ProtectedRoute requiredRole="agent">
                <AgentDashboard expectedAgentLevel={2} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/dashboard"
            element={
              <ProtectedRoute requiredRole="agent">
                <AgentDashboard expectedAgentLevel={3} />
              </ProtectedRoute>
            }
          />

          {/* 前台商城页面 (公开) */}
          <Route path="/shop" element={<Mall />} />
          <Route path="/shop.html" element={<Mall />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />

          {/* 默认重定向 */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;

